import { AccountFeatures } from "@enums/account/AccountFeature";
import { Ng1StateDeclaration, StateProvider } from "@uirouter/angularjs";
import * as angular from "angular";

export interface ICreateStateArgs {
    stateName: string;
    translation?: string;
    subtitleTranslation?: string;
    component?: string;
    url?: string;
}

export interface IRouteAuthRules {
    roles?: string[];
    features?: AccountFeatures[],
    location?: string,
}

export interface ICreateAuthorisedStateArgs extends ICreateStateArgs, IRouteAuthRules {
}

export interface ICreateDetailStateArgs extends ICreateStateArgs {
    bindingName?: string;
    breadcrumbNameResolverKey?: string
    queryParamsNames?: string[]
}

export interface ICreateTabSubRouteArgs extends ICreateAuthorisedStateArgs {
    icon: string;
}

export interface IStateData {
    translation: string;
    subtitleTranslationKey?: string;
    icon: string;
    roles: string[];
    countKey?: string,
    location?: string,
    features: AccountFeatures[];
}

export interface IRouteConfigHelperProvider {
    createVeriTabSubRoute(args: ICreateTabSubRouteArgs);
    createVeriTabRoute(args: ICreateAuthorisedStateArgs);

    createViewWithBoundParams(viewName: string, args: ICreateAuthorisedStateArgs): Ng1StateDeclaration;

    createState(args: ICreateStateArgs);
    createDetailState(args: ICreateDetailStateArgs);
    createDetailStateWithBoundParams(args: ICreateDetailStateArgs);
}

export class RouteConfigHelperProvider implements angular.IServiceProvider, IRouteConfigHelperProvider {
    static $inject = ["$stateProvider"];

    constructor(private $stateProvider: StateProvider) {
    }

    $get = (): IRouteConfigHelperProvider => {
        return {
            createVeriTabSubRoute: (args: ICreateTabSubRouteArgs) => this.createVeriTabSubRoute(args),
            createVeriTabRoute: (args: ICreateAuthorisedStateArgs) => this.createVeriTabRoute(args),

            createViewWithBoundParams: (viewName: string, args: ICreateAuthorisedStateArgs) => this.createViewWithBoundParams(viewName, args),

            createState: (args: ICreateStateArgs) => this.createState(args),
            createDetailState: (args: ICreateDetailStateArgs) => this.createDetailState(args),
            createDetailStateWithBoundParams: (args: ICreateDetailStateArgs) => this.createDetailStateWithBoundParams(args)
        }
    };

    createState(args: ICreateStateArgs) {
        this.$stateProvider.state(args.stateName, {
            url: args.url,
            views: { "content@app": { component: args.component } },
            data: { translation: args.translation, subtitleTranslationKey: args.subtitleTranslation }
        });
    }

    createVeriTabSubRoute(args: ICreateTabSubRouteArgs) {
        var stateData: IStateData = {
            translation: args.translation,
            subtitleTranslationKey: args.subtitleTranslation,
            icon: args.icon,
            roles: args.roles,
            features: args.features,
            location: args.location
        };

        this.$stateProvider.state(args.stateName, {
            url: args.url,
            component: args.component,
            data: stateData
        });
    }

    createVeriTabRoute(args: ICreateAuthorisedStateArgs) {

        this.$stateProvider.state(args.stateName, {
            url: args.url,
            data: { translation: args.translation, preventBreadcrumbBack: true },
            views: { "content@app": { template: `<veri-route-tabs route="${args.stateName}"></veri-route-tabs>` } },
        });
    }

    createDetailState(args: ICreateDetailStateArgs) {

        this.$stateProvider.state(args.stateName, {
            url: `/${args.url}`,
            views: { "content-detail@app": { component: args.component } },
            data: { translation: args.translation, subtitleTranslationKey: args.subtitleTranslation, }
        });
    }

    createViewWithBoundParams(viewName: string, args: ICreateDetailStateArgs): Ng1StateDeclaration {
        const bindings = {};
        bindings[args.bindingName] = args.bindingName;

        const params = {};
        params[args.bindingName] = args.bindingName;

        args?.queryParamsNames?.forEach(param => {
            params[param] = null;
        })

        const state: Ng1StateDeclaration = {
            url: `${args.url || ""}/:${args.bindingName}${this.appendQueryParams(args?.queryParamsNames)}`,
            name: args.stateName,
            views: {},
            data: { translation: args.translation, breadcrumbNameResolverKey: args.breadcrumbNameResolverKey },
            params: params,
            resolve: {}
        };

        state.views[viewName] = { component: args.component, bindings: bindings };
        state.resolve[args.bindingName] = ["$stateParams", ($stateParams) => { return $stateParams[args.bindingName]; }];

        args?.queryParamsNames?.forEach(param => {
            state.resolve[param] = ["$stateParams", ($stateParams) => { return $stateParams[param]; }];
        });

        return state;
    }

    createDetailStateWithBoundParams(args: ICreateDetailStateArgs) {
        const state = this.createViewWithBoundParams("content-detail@app", args)
        this.$stateProvider.state(args.stateName, state);
    }

    // ---

    private appendQueryParams(params?: string[]): string {
        if (!params || params.length === 0) {
            return "";
        }

        const queryParams = params.join('&');
        return `?${queryParams}`;
    }
}
